/**
 * Incident Integration
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useIncidentStore } from '@/store/incident.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { incidentMutationToastMessage } from '@/lib/incident/incident-api-error.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { toUploadProgressHandler } from '@/lib/upload/percent-from-axios-progress.js';

const NS = 'incidentIncidents';

function tMsg(key, defaultValue, options = {}) {
    return i18next.t(`${NS}:${key}`, { defaultValue, ...options });
}

function tError(key, defaultValue) {
    return tMsg(`errors.${key}`, defaultValue);
}

function tSuccess(key, defaultValue, options = {}) {
    return tMsg(`messages.${key}`, defaultValue, options);
}

function linkUnlinkSuccessToast(type, ids = []) {
    const count = Array.isArray(ids) ? ids.length : 0;
    const isBulk = count > 1;
    const keyMap = {
        linkActions: isBulk ? 'linkActionsBulkSuccess' : 'linkActionsSuccess',
        unlinkActions: isBulk ? 'unlinkActionsBulkSuccess' : 'unlinkActionsSuccess',
        linkTickets: isBulk ? 'linkTicketsBulkSuccess' : 'linkTicketsSuccess',
        unlinkTickets: isBulk ? 'unlinkTicketsBulkSuccess' : 'unlinkTicketsSuccess',
        linkProblems: isBulk ? 'linkProblemsBulkSuccess' : 'linkProblemsSuccess',
        unlinkProblems: isBulk ? 'unlinkProblemsBulkSuccess' : 'unlinkProblemsSuccess',
        linkChangeRequests: isBulk ? 'linkChangeRequestsBulkSuccess' : 'linkChangeRequestsSuccess',
        unlinkChangeRequests: isBulk ? 'unlinkChangeRequestsBulkSuccess' : 'unlinkChangeRequestsSuccess',
    };
    const defaultMap = {
        linkActionsSuccess: 'Action linked',
        linkActionsBulkSuccess: `${count} actions linked`,
        unlinkActionsSuccess: 'Action unlinked',
        unlinkActionsBulkSuccess: `${count} actions unlinked`,
        linkTicketsSuccess: 'Ticket linked',
        linkTicketsBulkSuccess: `${count} tickets linked`,
        unlinkTicketsSuccess: 'Ticket unlinked',
        unlinkTicketsBulkSuccess: `${count} tickets unlinked`,
        linkProblemsSuccess: 'Problem linked',
        linkProblemsBulkSuccess: `${count} problems linked`,
        unlinkProblemsSuccess: 'Problem unlinked',
        unlinkProblemsBulkSuccess: `${count} problems unlinked`,
        linkChangeRequestsSuccess: 'Change request linked',
        linkChangeRequestsBulkSuccess: `${count} change requests linked`,
        unlinkChangeRequestsSuccess: 'Change request unlinked',
        unlinkChangeRequestsBulkSuccess: `${count} change requests unlinked`,
    };
    const msgKey = keyMap[type];
    toast.success(tSuccess(msgKey, defaultMap[msgKey], isBulk ? { count } : {}));
}

export class IncidentIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchAll(params = {}) {
        const store = useIncidentStore.getState();
        const tenantId = params.tenantId ?? useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: tError('tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        store.setLoading(true);
        store.clearError();

        try {
            const page = params.page ?? store.searchParams.page ?? 1;
            const pageSize = params.pageSize ?? store.searchParams.pageSize ?? 25;
            const queryParams = {
                ...params,
                tenantId,
                page,
                pageSize,
                ...mapColumnFiltersForQuery(params.columnFilters),
            };
            delete queryParams.columnFilters;
            const response = await this.service.getAll(queryParams);
            const list = Array.isArray(response.data) ? response.data : [];
            store.setItems(list);
            store.setSearchParams({ page, pageSize });

            const meta = response.meta || {};
            const total = Number(meta.total ?? list.length);
            const resolvedPageSize = Number(meta.pageSize ?? pageSize);
            store.setPaginationData({
                currentPage: Number(meta.page ?? page),
                totalPages: Number(meta.totalPages ?? Math.max(1, Math.ceil(total / resolvedPageSize))),
                totalItems: total,
                itemsPerPage: resolvedPageSize,
            });

            store.setUniqueValues(response.uniqueValues || {});

            return { success: true, data: list, meta: response.meta, uniqueValues: response.uniqueValues || {} };
        } catch {
            const msg = tError('loadFailed', { defaultValue: 'Could not load incident list' });
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchMonitoring(params = {}) {
        try {
            const response = await this.service.getMonitoring(params);
            return { success: true, data: response.data };
        } catch {
            const msg = tError('monitoringLoadFailed', { defaultValue: 'Could not load incident monitoring data' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch {
            const msg = tError('getByIdFailed', { defaultValue: 'Could not load incident' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getDetail(id) {
        try {
            const response = await this.service.getDetail(id);
            return { success: true, data: response.data };
        } catch {
            const msg = tError('detailFailed', { defaultValue: 'Could not load incident detail' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async checkCodeAvailability(code, excludeId = null) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, available: true };
        }
        const trimmed = String(code || '').trim();
        if (!trimmed) {
            return { success: true, available: true };
        }
        try {
            const response = await this.service.checkCodeAvailability({
                code: trimmed,
                ...(excludeId ? { excludeId } : {}),
            });
            return {
                success: true,
                available: response.data?.available !== false,
            };
        } catch (error) {
            return { success: false, available: true, error: error.message };
        }
    }

    async create(data) {
        try {
            const response = await this.service.create(data);
            toast.success(tSuccess('createSuccess', { defaultValue: 'Incident created' }));
            return { success: true, data: response.data };
        } catch {
            const msg = tError('createFailed', { defaultValue: 'Could not create incident' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async update(id, data) {
        try {
            const response = await this.service.update(id, data);

            if (this._isPendingApproval(response)) {
                toast.info(
                    tSuccess('updatePendingApproval', { defaultValue: 'Change submitted to approval workflow' }),
                );
                return {
                    success: true,
                    pendingApproval: true,
                    pendingChangeId: response.pendingChangeId || response.data?.pendingChangeId,
                    workflowInstanceId: response.data?.workflowInstanceId,
                };
            }

            toast.success(tSuccess('updateSuccess', { defaultValue: 'Incident updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('updateFailed', { defaultValue: 'Could not update incident' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    _isPendingApproval(response) {
        const status = response?.approvalStatus || response?.data?.approvalStatus;
        if (status === 'submitted') return true;
        return Boolean(response?.pendingChangeId || response?.data?.pendingChangeId) && status !== 'draft';
    }

    async submitPendingUpdate(id) {
        try {
            const response = await this.service.submitPendingUpdate(id);
            toast.info(
                tSuccess('submitPendingSuccess', { defaultValue: 'Change submitted to approval workflow' }),
            );
            return {
                success: true,
                pendingApproval: true,
                pendingChangeId: response.pendingChangeId || response.data?.pendingChangeId,
                workflowInstanceId: response.data?.workflowInstanceId,
            };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('updateFailed', { defaultValue: 'Could not update incident' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async delete(id) {
        try {
            const response = await this.service.delete(id);
            if (response?.pending || response?.data?.pending) {
                toast.info(tSuccess('deletePendingApproval', { defaultValue: 'Delete request sent for approval' }));
                return { success: true, pendingApproval: true };
            }
            toast.success(tSuccess('deleteSuccess', { defaultValue: 'Incident deleted' }));
            return { success: true };
        } catch {
            const msg = tError('deleteFailed', { defaultValue: 'Could not delete incident' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getRelatedActions(incidentId, params = {}) {
        try {
            const response = await this.service.getRelatedActions(incidentId, params);
            return { success: true, data: response.data, meta: response.meta };
        } catch {
            const msg = tError('actionsLoadFailed', { defaultValue: 'Could not load linked actions' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getLinkableActions(incidentId, params = {}) {
        try {
            const response = await this.service.getLinkableActions(incidentId, params);
            return { success: true, data: response.data, meta: response.meta };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async linkActions(incidentId, actionIds, opts = {}) {
        try {
            const response = await this.service.linkActions(incidentId, actionIds, opts);
            if (!opts.silent) {
                linkUnlinkSuccessToast('linkActions', actionIds);
            }
            return { success: true, data: response.data };
        } catch {
            const msg = tError('linkActionsFailed', { defaultValue: 'Could not link actions' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkActions(incidentId, actionIds, opts = {}) {
        try {
            const response = await this.service.unlinkActions(incidentId, actionIds);
            if (!opts.silent) {
                linkUnlinkSuccessToast('unlinkActions', actionIds);
            }
            return { success: true, data: response.data };
        } catch {
            const msg = tError('unlinkActionsFailed', { defaultValue: 'Could not unlink action' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkAction(incidentId, actionId, opts = {}) {
        return this.unlinkActions(incidentId, [actionId], opts);
    }

    async reorderActions(incidentId, { actionIds } = {}) {
        try {
            const response = await this.service.reorderActions(incidentId, { actionIds });
            toast.success(tSuccess('reorderActionsSuccess', { defaultValue: 'Action order updated' }));
            return { success: true, data: response.data };
        } catch {
            const msg = tError('reorderActionsFailed', { defaultValue: 'Could not update action order' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getRelatedTickets(incidentId, params = {}) {
        try {
            const response = await this.service.getRelatedTickets(incidentId, params);
            return { success: true, data: response.data, meta: response.meta };
        } catch {
            const msg = tError('ticketsLoadFailed', { defaultValue: 'Could not load related tickets' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getLinkableTickets(incidentId, params = {}) {
        try {
            const response = await this.service.getLinkableTickets(incidentId, params);
            return { success: true, data: response.data, meta: response.meta };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async linkTickets(incidentId, ticketIds, opts = {}) {
        try {
            const response = await this.service.linkTickets(incidentId, ticketIds);
            if (!opts.silent) {
                linkUnlinkSuccessToast('linkTickets', ticketIds);
            }
            return { success: true, data: response.data };
        } catch {
            const msg = tError('linkTicketsFailed', { defaultValue: 'Could not link tickets' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkTickets(incidentId, ticketIds, opts = {}) {
        try {
            const response = await this.service.unlinkTickets(incidentId, ticketIds);
            if (!opts.silent) {
                linkUnlinkSuccessToast('unlinkTickets', ticketIds);
            }
            return { success: true, data: response.data };
        } catch {
            const msg = tError('unlinkTicketsFailed', { defaultValue: 'Could not unlink ticket' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkTicket(incidentId, ticketId, opts = {}) {
        return this.unlinkTickets(incidentId, [ticketId], opts);
    }

    async getRelatedProblems(incidentId, params = {}) {
        try {
            const response = await this.service.getRelatedProblems(incidentId, params);
            return { success: true, data: response.data, meta: response.meta };
        } catch {
            const msg = tError('problemsLoadFailed', { defaultValue: 'Could not load related problems' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getLinkableProblems(incidentId, params = {}) {
        try {
            const response = await this.service.getLinkableProblems(incidentId, params);
            return { success: true, data: response.data, meta: response.meta };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async linkProblems(incidentId, problemIds, opts = {}) {
        try {
            const response = await this.service.linkProblems(incidentId, problemIds);
            if (!opts.silent) {
                linkUnlinkSuccessToast('linkProblems', problemIds);
            }
            return { success: true, data: response.data };
        } catch {
            const msg = tError('linkProblemsFailed', { defaultValue: 'Could not link problems' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkProblems(incidentId, problemIds, opts = {}) {
        try {
            const response = await this.service.unlinkProblems(incidentId, problemIds);
            if (!opts.silent) {
                linkUnlinkSuccessToast('unlinkProblems', problemIds);
            }
            return { success: true, data: response.data };
        } catch {
            const msg = tError('unlinkProblemsFailed', { defaultValue: 'Could not unlink problem' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkProblem(incidentId, problemId, opts = {}) {
        return this.unlinkProblems(incidentId, [problemId], opts);
    }

    async getRelatedChangeRequests(incidentId, params = {}) {
        try {
            const response = await this.service.getRelatedChangeRequests(incidentId, params);
            return { success: true, data: response.data, meta: response.meta };
        } catch {
            const msg = tError('changeRequestsLoadFailed', { defaultValue: 'Could not load related change requests' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getLinkableChangeRequests(incidentId, params = {}) {
        try {
            const response = await this.service.getLinkableChangeRequests(incidentId, params);
            return { success: true, data: response.data, meta: response.meta };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async linkChangeRequests(incidentId, changeRequestIds, opts = {}) {
        try {
            const response = await this.service.linkChangeRequests(incidentId, changeRequestIds);
            if (!opts.silent) {
                linkUnlinkSuccessToast('linkChangeRequests', changeRequestIds);
            }
            return { success: true, data: response.data };
        } catch {
            const msg = tError('linkChangeRequestsFailed', { defaultValue: 'Could not link change requests' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkChangeRequests(incidentId, changeRequestIds, opts = {}) {
        try {
            const response = await this.service.unlinkChangeRequests(incidentId, changeRequestIds);
            if (!opts.silent) {
                linkUnlinkSuccessToast('unlinkChangeRequests', changeRequestIds);
            }
            return { success: true, data: response.data };
        } catch {
            const msg = tError('unlinkChangeRequestsFailed', { defaultValue: 'Could not unlink change request' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkChangeRequest(incidentId, changeRequestId, opts = {}) {
        return this.unlinkChangeRequests(incidentId, [changeRequestId], opts);
    }

    async getComments(incidentId, params = {}) {
        try {
            const response = await this.service.getComments(incidentId, params);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch {
            const msg = tError('commentsLoadFailed', { defaultValue: 'Could not load comments' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async addComment(incidentId, data) {
        try {
            const response = await this.service.addComment(incidentId, data);
            toast.success(tSuccess('commentAddSuccess', { defaultValue: 'Comment added' }));
            return { success: true, data: response.data };
        } catch {
            const msg = tError('commentAddFailed', { defaultValue: 'Could not add comment' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async updateComment(incidentId, commentId, data) {
        try {
            const response = await this.service.updateComment(incidentId, commentId, data);
            toast.success(tSuccess('commentUpdateSuccess', { defaultValue: 'Comment updated' }));
            return { success: true, data: response.data };
        } catch {
            const msg = tError('commentUpdateFailed', { defaultValue: 'Could not update comment' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async deleteComment(incidentId, commentId) {
        try {
            await this.service.deleteComment(incidentId, commentId);
            toast.success(tSuccess('commentDeleteSuccess', { defaultValue: 'Comment deleted' }));
            return { success: true };
        } catch {
            const msg = tError('commentDeleteFailed', { defaultValue: 'Could not delete comment' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getAttachments(incidentId) {
        try {
            const response = await this.service.getAttachments(incidentId);
            return { success: true, data: response.data };
        } catch {
            const msg = tError('attachmentsLoadFailed', { defaultValue: 'Could not load attachments' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async uploadAttachment(incidentId, file, category = 'attachment', onProgress) {
        try {
            const response = await this.service.uploadAttachment(incidentId, file, category, {
                onUploadProgress: toUploadProgressHandler(onProgress),
            });
            toast.success(tSuccess('uploadSuccess', { defaultValue: 'File uploaded' }));
            return { success: true, data: response.data };
        } catch {
            const msg = tError('uploadFailed', { defaultValue: 'Could not upload file' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async deleteAttachment(incidentId, attachmentId) {
        try {
            await this.service.deleteAttachment(incidentId, attachmentId);
            toast.success(tSuccess('deleteAttachmentSuccess', { defaultValue: 'File deleted' }));
            return { success: true };
        } catch {
            const msg = tError('deleteAttachmentFailed', { defaultValue: 'Could not delete file' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async submit(id) {
        try {
            const response = await this.service.submit(id);
            const skipped = Boolean(response?.workflowSkipped);
            toast.success(
                tSuccess(
                    skipped ? 'submitSuccessNoWorkflow' : 'submitSuccess',
                    skipped
                        ? 'Incident approved (no workflow configured)'
                        : 'Incident submitted for review',
                ),
            );
            return { success: true, data: response.data, workflowSkipped: skipped };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('submitFailed', { defaultValue: 'Could not send' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async assign(id, data) {
        try {
            const response = await this.service.assign(id, data);
            toast.success(tSuccess('assignSuccess', { defaultValue: 'Incident assigned' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('assignFailed', { defaultValue: 'Assignment failed' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async claim(id) {
        try {
            const response = await this.service.claim(id);
            toast.success(tSuccess('claimSuccess', { defaultValue: 'Task claimed' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('claimFailed', { defaultValue: 'Could not claim task' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async acceptAssignment(id) {
        try {
            const response = await this.service.acceptAssignment(id);
            toast.success(tSuccess('acceptAssignmentSuccess', { defaultValue: 'Task accepted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('acceptAssignmentFailed', { defaultValue: 'Could not accept task' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async rejectAssignment(id, data) {
        try {
            const response = await this.service.rejectAssignment(id, data);
            toast.success(tSuccess('rejectAssignmentSuccess', { defaultValue: 'Task rejected' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('rejectAssignmentFailed', { defaultValue: 'Could not reject task' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async resolve(id, data = {}) {
        try {
            const response = await this.service.resolve(id, data);
            if (response?.pending) {
                toast.info(
                    tSuccess('resolvePending', { defaultValue: 'Resolve action submitted to approval workflow. You will be notified of the result.' }),
                );
                return { success: true, pending: true, data: response.data };
            }
            toast.success(tSuccess('resolveSuccess', { defaultValue: 'Incident resolved' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('resolveFailed', { defaultValue: 'Resolve failed' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async close(id, data = {}) {
        try {
            const response = await this.service.close(id, data);
            if (response?.pending) {
                toast.info(
                    tSuccess('closePending', { defaultValue: 'Close action submitted to approval workflow. You will be notified of the result.' }),
                );
                return { success: true, pending: true, data: response.data };
            }
            toast.success(tSuccess('closeSuccess', { defaultValue: 'Incident closed' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('closeFailed', { defaultValue: 'Close failed' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async fetchWorkflowTemplateOptions(params = { includeInactive: true }) {
        try {
            const response = await this.service.getWorkflowTemplateOptions(params);
            return { success: response.success !== false, data: response.data || [] };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('workflowOptionsFailed', { defaultValue: 'Could not load template options' }));
            toast.error(msg);
            return { success: false, data: [], error: msg };
        }
    }

    async getWorkflowTemplateOptions(params = {}) {
        try {
            const response = await this.service.getWorkflowTemplateOptions(params);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('workflowOptionsFailed', { defaultValue: 'Could not load template options' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async fetchIncidentPartyTemplate(params = {}) {
        try {
            const res = await this.service.getPartyTemplate(params);
            return { success: res.success !== false, data: res.data ?? null };
        } catch {
            return { success: false, data: null };
        }
    }

    async fetchIncidentParty(incidentId) {
        if (!incidentId) return { success: false, data: null, partyTemplate: null };
        try {
            const res = await this.service.getParty(incidentId);
            return {
                success: res.success !== false,
                data: res.data ?? null,
                partyTemplate: res.partyTemplate ?? null,
            };
        } catch {
            return { success: false, data: null, partyTemplate: null };
        }
    }

    async syncIncidentParty(incidentId, payload, options = {}) {
        const { silentSuccess = false } = options;
        try {
            const res = await this.service.syncParty(incidentId, payload);
            if (!silentSuccess) {
                toast.success(
                    i18next.t('incidentIncidents:ownership.saveSuccess', {
                            defaultValue: 'Roles saved',
                        }),
                );
            }
            return { success: true, data: res.data };
        } catch (error) {
            const msg = incidentMutationToastMessage(error, tError('partySyncFailed', { defaultValue: 'Could not save roles' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }
}
